// TradeSettingsDialog — the trading presets, on Soft (operator 2026-10-12: one
// preset set for everything, and its settings are a modal, not a fly-out). The
// gear of the rail's form opens it, and Instant trade's; it took the place of
// the fly-out the rail grew under its preset row.
//
// It edits the one saved set (lib/tradePresetStore), so a figure saved here is
// the slippage the rail, Instant trade, a list's Quick buy and a new limit
// order go out on, at once and after a reload — which is why a field saves
// when it is left, not on every key (PercentField). In the manner of
// Customize, in the shared FilterDialog: a head (the name, the reset, the close),
// a track of the three presets, the open preset's settings as rows — a mark,
// the label and its hint at the left, the field at the right — and under them
// a plate with what that preset trades on.
//
// A preset holds what it held: buy slippage, sell slippage, the cap. The
// fields are the fly-out's three, held to its bounds (a percent to the nearest
// basis point, 0 to MAX_SLIPPAGE_BPS — slippageBpsFromPercent). Two things the
// fly-out did are not done: it printed the saved figure back on every key, so
// a fraction could not be typed ("1." became "1"), and it wrote 0 the moment a
// field was emptied. A field keeps what is typed while it is being typed; an
// empty one writes nothing, and the saved figure comes back when it is left.
//
// The tab is what is looked at, not what is traded on: choosing a preset is
// the form's own control. The preset the opening surface is on is the tab the
// dialog opens on, and it is marked.

import {
  ArrowCounterClockwiseIcon,
  ArrowLeftIcon,
  ArrowLineUpIcon,
  ArrowUUpLeftIcon,
  type Icon,
  SwapIcon,
  TrendDownIcon,
  TrendUpIcon,
  XIcon,
} from '@phosphor-icons/react'
import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import {
  DEFAULT_TRADE_PRESETS,
  MAX_SLIPPAGE_BPS,
  type TradePreset,
  slippageBpsFromPercent,
  slippageForSide,
} from '../../lib/tradePanel'
import {
  readTradePresets,
  resetTradePresets,
  useTradePresets,
  writeTradePresets,
} from '../../lib/tradePresetStore'
import soft from '../../styles/soft.module.css'
import { Described } from '../ui/Described'
import { FilterDialog } from '../ui/FilterDialog'
import styles from './TradeSettingsDialog.module.css'

interface Props {
  /** The preset the surface that opened the dialog is on: the tab it opens on,
   *  marked as the one in use. `null`: opened from a place that is on no
   *  preset (the token page's Page settings) — the first tab, and no mark. */
  preset?: number | null
  onClose: () => void
  /** Given when the dialog was opened from another dialog that shut to make
   *  room for it (Instant trade's settings): a Back stands first in the head
   *  and returns there, and so do Escape and a press outside — one step back,
   *  never both dialogs at once. Close still shuts everything. */
  onBack?: () => void
}

const SETTINGS: readonly {
  key: keyof TradePreset
  label: string
  hint: string
  Icon: Icon
  tone?: 'buy' | 'sell'
}[] = [
  {
    key: 'buySlippageBps',
    label: 'Buy slippage',
    hint: 'How far the price may move on a buy',
    Icon: TrendUpIcon,
    tone: 'buy',
  },
  {
    key: 'sellSlippageBps',
    label: 'Sell slippage',
    hint: 'How far the price may move on a sell',
    Icon: TrendDownIcon,
    tone: 'sell',
  },
  {
    key: 'maxSlippageBps',
    label: 'Max slippage cap',
    hint: 'Neither side goes out above it',
    Icon: ArrowLineUpIcon,
  },
]

const percent = (bps: number) => `${bps / 100}%`

// what is typed, held to a figure: digits and one point (a comma is a point)
function cleaned(text: string): string {
  const figure = text.replace(/,/g, '.').replace(/[^0-9.]/g, '')
  const point = figure.indexOf('.')
  return point < 0
    ? figure
    : `${figure.slice(0, point + 1)}${figure.slice(point + 1).replace(/\./g, '')}`
}

// the percent a text says, or null while it says none ("", ".")
function typedPercent(text: string): number | null {
  if (!/[0-9]/.test(text)) return null
  const value = Number(text)
  return Number.isFinite(value) ? value : null
}

// what a held side says under the plate
function heldNote(buyHeld: boolean, sellHeld: boolean): string | null {
  if (buyHeld && sellHeld) return 'Buy and sell are held by the cap'
  if (buyHeld) return 'Buy is held by the cap'
  if (sellHeld) return 'Sell is held by the cap'
  return null
}

export function TradeSettingsDialog({ preset = 0, onClose, onBack }: Props) {
  const presets = useTradePresets()
  // -1: no surface's preset to mark (preset === null)
  const inUse =
    preset === null
      ? -1
      : Number.isInteger(preset) && preset >= 0 && preset < presets.length
        ? preset
        : 0
  const [tab, setTab] = useState(Math.max(inUse, 0))
  const panel = useRef<HTMLDivElement>(null)
  // What was saved when Reset was pressed. Reset stands beside Close and takes
  // all three presets at once — a press meant for Close would quietly change
  // what every trade goes out on — so the same button, in the same place, puts
  // them back. It is an undo only while nothing has been saved since: an edit
  // made after the reset, here or in another tab, is the newer word.
  const [beforeReset, setBeforeReset] = useState<readonly TradePreset[] | null>(null)
  const canUndo = beforeReset !== null && presets === DEFAULT_TRADE_PRESETS

  // Instant trade's hotkeys listen on the document: 1–4 fire a buy, unless the
  // key was typed into a field. While this dialog is open no key may reach a
  // shortcut of the page under it. A key pressed inside the panel is stopped
  // where React hands it up (keepKey below), so the fields' own handlers run
  // first; one pressed with the focus nowhere — a click on the dialog's ground
  // leaves it on the page's body — is stopped here, on its way down.
  useEffect(() => {
    const stop = (e: globalThis.KeyboardEvent) => {
      if (!panel.current?.contains(e.target as Node)) e.stopPropagation()
    }
    window.addEventListener('keydown', stop, true)
    return () => window.removeEventListener('keydown', stop, true)
  }, [])

  // A modal opens with the focus on its first control, and here that is Reset
  // to defaults: Enter or Space pressed out of habit would reset every preset.
  // The focus starts on the open preset's tab instead, where a press changes
  // nothing. (This runs after FilterDialog has shown the dialog.)
  useEffect(() => {
    panel.current?.querySelector<HTMLElement>('[role="group"] [aria-pressed="true"]')?.focus()
  }, [])
  const keepKey = (e: KeyboardEvent) => e.stopPropagation()

  const shown = presets[tab] ?? presets[0]
  if (!shown) return null

  // Onto the set as it is saved NOW, not as this render saw it: a field can
  // commit after its render (when the dialog closes), and two that do must not
  // write over each other.
  const save = (key: keyof TradePreset, bps: number) =>
    writeTradePresets(readTradePresets().map((p, i) => (i === tab ? { ...p, [key]: bps } : p)))
  const reset = () => {
    if (canUndo) {
      writeTradePresets(beforeReset)
      setBeforeReset(null)
      return
    }
    // with nothing saved there is nothing to put back
    setBeforeReset(presets === DEFAULT_TRADE_PRESETS ? null : presets)
    resetTradePresets()
  }
  const buy = slippageForSide(shown, 'buy')
  const sell = slippageForSide(shown, 'sell')
  const held = heldNote(buy < shown.buySlippageBps, sell < shown.sellSlippageBps)

  return (
    <FilterDialog label="Trade settings" onClose={onBack ?? onClose}>
      <div ref={panel} className={styles.panel} onKeyDown={keepKey}>
        <header className={styles.head}>
          {onBack ? (
            <Described
              as="button"
              type="button"
              className={`${soft.btn} ${soft.round} ${soft.small} ${styles.back}`}
              onClick={onBack}
              aria-label="Back"
              description="Back to Instant trade settings"
            >
              <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
            </Described>
          ) : (
            <span className={styles.headMark} aria-hidden="true">
              <SwapIcon size={18} weight="bold" aria-hidden="true" />
            </span>
          )}
          <h2 className={styles.title}>Trade settings</h2>
          <div className={styles.headActions}>
            <Described
              as="button"
              type="button"
              className={`${soft.btn} ${soft.round} ${soft.small}`}
              onClick={reset}
              aria-label={canUndo ? 'Undo reset' : 'Reset to defaults'}
              description={
                canUndo
                  ? 'Put the presets back as they were'
                  : 'Reset all three presets to defaults'
              }
            >
              {canUndo ? (
                <ArrowUUpLeftIcon size={16} weight="bold" aria-hidden="true" />
              ) : (
                <ArrowCounterClockwiseIcon size={16} weight="bold" aria-hidden="true" />
              )}
            </Described>
            <button
              type="button"
              className={`${soft.btn} ${soft.round} ${soft.small}`}
              onClick={onClose}
              aria-label="Close"
            >
              <XIcon size={16} weight="bold" aria-hidden="true" />
            </button>
          </div>
        </header>
        {/* biome-ignore lint/a11y/useSemanticElements: a fieldset would bring its own frame and legend into a row of three */}
        <div className={`${soft.track} ${styles.tabs}`} role="group" aria-label="Preset">
          {presets.map((_, i) => (
            <Described
              as="button"
              // biome-ignore lint/suspicious/noArrayIndexKey: the presets are a fixed list told apart by their place
              key={i}
              type="button"
              className={`${soft.seg} ${styles.tab}`}
              aria-pressed={i === tab}
              aria-label={i === inUse ? `Preset ${i + 1}, in use` : undefined}
              description={i === inUse ? 'The preset in use here' : undefined}
              onClick={() => setTab(i)}
            >
              <span className={soft.fig}>Preset {i + 1}</span>
              {i === inUse && <i className={styles.inUse} />}
            </Described>
          ))}
        </div>
        {/* a tab's fields are its own: what was being typed does not follow to the next */}
        <div className={styles.body} key={tab}>
          {SETTINGS.map(({ key, label, hint, Icon, tone }) => (
            <section key={key} className={styles.setting} aria-label={label}>
              <span className={styles.settingMark} data-tone={tone} aria-hidden="true">
                <Icon size={16} weight="bold" aria-hidden="true" />
              </span>
              <div className={styles.settingText}>
                <h3 className={styles.settingLabel}>{label}</h3>
                <Described
                  as="span"
                  className={styles.settingHint}
                  description={`0 to ${percent(MAX_SLIPPAGE_BPS)}`}
                >
                  {hint}
                </Described>
              </div>
              <PercentField
                name={`${label} percent`}
                bps={shown[key]}
                onSave={(bps) => save(key, bps)}
              />
            </section>
          ))}
        </div>
        <div className={styles.sends}>
          <span className={styles.sendsLabel}>Preset {tab + 1} trades on</span>
          <span className={styles.sendsValue}>
            <span className={styles.sendsSide} data-tone="buy">
              Buy
            </span>
            <b data-sends="buy">{percent(buy)}</b>
          </span>
          <span className={styles.sendsValue}>
            <span className={styles.sendsSide} data-tone="sell">
              Sell
            </span>
            <b data-sends="sell">{percent(sell)}</b>
          </span>
        </div>
        {/* the line is always there, so the dialog does not grow when a side is held */}
        <p className={styles.note} data-held={held ? '' : undefined}>
          {held ??
            (canUndo
              ? 'Reset to defaults — press the button again to undo'
              : 'Used by the form, Instant trade, Quick buy and new limit orders')}
        </p>
      </div>
    </FilterDialog>
  )
}

// One figure of a preset, in percent. While it is being typed it shows what is
// typed; otherwise the saved figure.
//
// MONEY PATH — WHEN it is saved (review of round 5). The saved set is the
// slippage every trade surface goes out on, here and in the other tabs, from
// the moment it is written. A field used to write on every key, so "25" typed
// on the way to "2.5" was, for that moment, a live 25% everywhere. A field
// commits what was typed — held to the bounds; text that says no figure saves
// nothing — when it is LEFT for another control, on Enter, and when the dialog
// closes or another preset's tab is opened (the field unmounts). Never while
// it is being typed, and not because the window lost the focus (the blur
// handler, below). Escape in a field drops what was typed: the saved figure
// is back.
//
// What is typed is the field's only while the saved figure is still the one it
// was typed over: a reset, or an edit from another tab, puts the saved figure
// back in the field, and nothing typed before it is committed after it.
function PercentField({
  name,
  bps,
  onSave,
}: {
  name: string
  bps: number
  onSave: (bps: number) => void
}) {
  const [entry, setEntry] = useState<{ text: string; bps: number } | null>(null)
  const typed = entry && entry.bps === bps ? entry.text : null
  const figure = typed === null ? null : typedPercent(typed)
  const over = figure !== null && figure * 100 > MAX_SLIPPAGE_BPS
  // what a commit would save now (null: nothing typed, or no figure), with the
  // saved figure and the save of this render — read by the commits that do not
  // run in it: the blur Enter makes, and the unmount
  const unsaved = figure === null ? null : slippageBpsFromPercent(figure)
  const live = useRef({ unsaved, bps, onSave })
  live.current = { unsaved, bps, onSave }
  // at most once for what was typed: a commit takes it
  const commit = useCallback(() => {
    const { unsaved: next, bps: saved, onSave: save } = live.current
    live.current = { ...live.current, unsaved: null }
    setEntry(null)
    if (next !== null && next !== saved) save(next)
  }, [])
  // the dialog closes, or another preset's tab is opened: the field is gone
  useEffect(() => commit, [commit])
  const discard = () => {
    live.current = { ...live.current, unsaved: null }
    setEntry(null)
  }
  return (
    <label className={styles.field} data-over={over || undefined}>
      <input
        aria-label={name}
        aria-invalid={over || undefined}
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        value={typed ?? String(bps / 100)}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setEntry({ text: cleaned(e.target.value), bps })}
        // Left for another control, or for the page: committed. The WINDOW
        // losing the focus (another app, another tab) blurs the field too, and
        // is not that — the field is still the document's active element, the
        // one with the caret when the window is back; what is half typed in
        // it must not go live because of an alt-tab.
        onBlur={(e) => {
          if (document.activeElement !== e.currentTarget) commit()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit()
            e.currentTarget.blur()
          } else if (e.key === 'Escape' && typed !== null) {
            // the key is the field's: it does not also close the dialog
            e.preventDefault()
            discard()
          }
        }}
      />
      <span aria-hidden="true">%</span>
    </label>
  )
}
